import type { Options } from '@sentry/types';

type Arguments = {
    dsn: string;
    environment: 'dev' | 'prod';
    isDeployed: boolean;
    release: string;
};

const eventLimiter: { [msg: string]: boolean } = {};

export default ({ dsn, environment, isDeployed, release }: Arguments): Options => ({
    dsn,
    environment,
    release,
    beforeSend(event, hint) {
        if (isDeployed) {
            // Limit events to 1 per minute.
            // Adapted from <https://github.com/getsentry/sentry-javascript/issues/435#issuecomment-605634518>.
            const originalException = hint?.originalException;
            const msg =
                typeof originalException === 'string'
                    ? originalException
                    : originalException?.message || 'unknown error';

            // do not send event if already sent in last 1 minute
            if (msg in eventLimiter) {
                console.error('[Sentry] Rate limiting activated for', msg);
                return null;
            }

            eventLimiter[msg] = true;

            setTimeout(() => {
                delete eventLimiter[msg];
            }, 60000);

            return event;
        }

        const exception = hint && hint.originalException;
        if (exception) {
            console.error(
                'CAUGHT BY SENTRY:',
                typeof exception === 'string' ? exception : exception.message,
            );
            event.extra && console.error('extra info:', event.extra);
            typeof exception !== 'string' && console.error(exception);
        } else if (typeof event.message === 'string') {
            console.info(event.message); // eslint-disable-line no-console
        }
        return null;
    },
});
